import React, { createContext, useContext, useMemo } from 'react'
import { useGetLessonOverviewQuery } from 'util/rtk-api'
import { LessonOverview } from '@all-for-one/shared-types'

const DIFFICULTY_ORDER = ['Emerging', 'Intermediate', 'Advanced']

type LessonOverviewContextType = {
  data?: LessonOverview
  isLoading: boolean
  error?: any
  difficultyFilter?: string
  allDifficultiesWithData: string[]
}

const LessonOverviewContext = createContext<LessonOverviewContextType | null>(null)

export const useLessonOverview = () => {
  const ctx = useContext(LessonOverviewContext)
  if (!ctx) throw new Error('useLessonOverview must be used within LessonOverviewProvider')
  return ctx
}

export const LessonOverviewProvider = ({
  children,
  classId,
  lessonId,
  difficulty,
}: {
  children: React.ReactNode
  classId: string
  lessonId: string
  difficulty?: string
}) => {
  const { data, isLoading, error } = useGetLessonOverviewQuery({ classId, lessonId })

  const allDifficultiesWithData = useMemo(() => {
    if (!data) return []
    const set = new Set(data.questions.map((q: any) => q.difficulty).filter(Boolean))
    return DIFFICULTY_ORDER.filter(d => set.has(d))
  }, [data])

  const filtered = useMemo(() => {
    if (!data) return data
    if (!difficulty || difficulty === 'All') return data
    return {
      ...data,
      questions: data.questions.filter(q => q.difficulty === difficulty)
    }
  }, [data, difficulty])

  return (
    <LessonOverviewContext.Provider value={{ data: filtered, isLoading, error, difficultyFilter: difficulty, allDifficultiesWithData }}>
      {children}
    </LessonOverviewContext.Provider>
  )
}


